import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCurrentPeriod,
  fetchAssessmentCalendar,
  selectCurrentPeriod,
  selectAssessmentCalendar,
  selectCalendarLoading,
  fetchSchoolYearCalendar,
  selectSchoolYearCalendar,
  fetchAssessmentWindows,
  selectAssessmentDistrictWindows,
} from "@/store/slices/CalendarSlice";
import { useRosterInfo } from "./useRosterInfo";
import { AppDispatch } from "@/models/AppDispatch.type";
import { ENTITY_TYPE } from "@/services/assignment/constants";

const dispatched = {
  currentPeriod: false,
  assessmentCalendar: false,
  schoolYearCalendar: false,
  assessmentWindows: false,
}

export const useCalendar = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Get districtId and schoolId from useRosterInfo hook
  const { districtId, schoolId } = useRosterInfo();

  const currentPeriod = useSelector(selectCurrentPeriod);
  const assessmentCalendar = useSelector(selectAssessmentCalendar);
  const schoolYearCalendar = useSelector(selectSchoolYearCalendar);
  const assessmentDistrictWindows = useSelector(selectAssessmentDistrictWindows);
  const loading = useSelector(selectCalendarLoading);

  // Fetch current period and assessment calendar on initial load or when schoolId changes
  useEffect(() => {
    if (!schoolId) {
      return;
    }

    // Only fetch if we don't already have the current period
    if (!currentPeriod && !dispatched.currentPeriod) {
      dispatch(fetchCurrentPeriod(schoolId))
      dispatched.currentPeriod = true;
    }
    // Only fetch if we don't already have the assessment calendar
    if (!assessmentCalendar && !dispatched.assessmentCalendar) {
      dispatch(fetchAssessmentCalendar({
        schoolId: `${schoolId}`,
      }) as any)
      dispatched.assessmentCalendar = true;
    }

    if (!schoolYearCalendar && !dispatched.schoolYearCalendar) {
      dispatch(fetchSchoolYearCalendar({
        districtId: `${districtId}`,
      }) as any)
      dispatched.schoolYearCalendar = true;
    }

    if (!assessmentDistrictWindows && !dispatched.assessmentWindows && schoolYearCalendar) {
      const startDate = schoolYearCalendar?.startDate!
      const endDate = schoolYearCalendar?.endDate!
      if (startDate && endDate) {
        dispatch(fetchAssessmentWindows({
          entityId: `${districtId}`,
          entityType: ENTITY_TYPE.DISTRICT,
          dateFrom: startDate,
          dateTo: endDate,
        }) as any)
        dispatched.assessmentWindows = true;
      }
    }
  }, [dispatch, schoolId, currentPeriod, assessmentCalendar, schoolYearCalendar]);

  useEffect(() => {
    if (currentPeriod && assessmentCalendar && schoolYearCalendar && assessmentDistrictWindows) {
      dispatched.currentPeriod = false;
      dispatched.assessmentCalendar = false;
      dispatched.schoolYearCalendar = false;
      dispatched.assessmentWindows = false;
    }
  }, [currentPeriod, assessmentCalendar, schoolYearCalendar, assessmentDistrictWindows]);

  return {
    currentPeriod,
    schoolYearCalendar,
    assessmentCalendar,
    assessmentDistrictWindows,
    loading,
    schoolId
  };
};